import React, { useState, useEffect, useCallback } from 'react';
import { Box, Chip, Tooltip, Typography, CircularProgress } from '@mui/material';
import { Shield as ShieldIcon, FiberManualRecord as DotIcon } from '@mui/icons-material';
import { dashboardAPI } from '../../services/api';

/**
 * AI failsafe routing indicator (Primary / Fallback).
 * variant="card" — compact row for Generation Performance section.
 */
const AiFailsafeStatusChip = ({ pollIntervalMs = 30000, variant = 'chip' }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    try {
      const res = await dashboardAPI.getAiFailsafeStatus();
      if (res.data?.success) {
        setStatus(res.data);
      }
    } catch (err) {
      console.error('AI failsafe status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const id = setInterval(loadStatus, pollIntervalMs);
    return () => clearInterval(id);
  }, [loadStatus, pollIntervalMs]);

  if (loading && !status) {
    if (variant === 'card') {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.75,
            px: 1.25,
            borderRadius: 1.5,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Loading AI routing…
          </Typography>
        </Box>
      );
    }
    return (
      <Chip
        size="small"
        icon={<CircularProgress size={12} color="inherit" />}
        label="AI routing…"
        variant="outlined"
        sx={{ height: 24, fontSize: '0.7rem' }}
      />
    );
  }

  if (!status) {
    return null;
  }

  const isFallback = status.mode === 'FALLBACK';
  const sinceText = status.fallback_started_at_ist
    ? status.fallback_started_at_ist
    : null;

  const tooltip = isFallback
    ? [
        'Fallback — image jobs use OpenAI',
        `RPM ${status.secondary_rpm_used}/${status.secondary_rpm_limit} per min`,
        sinceText ? `Switched ${sinceText}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Primary — image jobs use NanoBanana';

  if (variant === 'card') {
    return (
      <Tooltip title={tooltip} arrow placement="top">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: { xs: 0.75, sm: 1.5 },
            py: 0.75,
            px: 1.25,
            borderRadius: 1.5,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: isFallback ? 'warning.light' : 'success.light',
            boxShadow: 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: 'text.disabled',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontSize: '0.6rem',
                flexShrink: 0,
              }}
            >
              AI routing
            </Typography>
            <DotIcon
              sx={{
                fontSize: 8,
                color: isFallback ? 'warning.main' : 'success.main',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                fontSize: '0.7rem',
                color: isFallback ? 'warning.dark' : 'success.dark',
                lineHeight: 1.2,
              }}
            >
              {isFallback ? 'Fallback' : 'Primary'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: '0.65rem',
                color: 'text.secondary',
                display: { xs: 'none', sm: 'inline' },
              }}
            >
              · {status.provider_hint}
            </Typography>
          </Box>
          {isFallback && sinceText && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                fontWeight: 600,
                color: 'text.secondary',
                lineHeight: 1.2,
              }}
            >
              Since {sinceText}
            </Typography>
          )}
          {!isFallback && (
            <Typography
              variant="caption"
              sx={{ fontSize: '0.65rem', color: 'text.disabled', fontWeight: 600 }}
            >
              NanoBanana active
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  }

  const label = isFallback ? 'AI · Fallback' : 'AI · Primary';

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
      <Tooltip title={tooltip} arrow>
        <Chip
          size="small"
          icon={<ShieldIcon sx={{ fontSize: '14px !important' }} />}
          label={label}
          color={isFallback ? 'warning' : 'success'}
          variant={isFallback ? 'filled' : 'outlined'}
          sx={{
            height: 24,
            fontSize: '0.7rem',
            fontWeight: 700,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      </Tooltip>
    </Box>
  );
};

export default AiFailsafeStatusChip;
